import React, { useMemo, useState, useEffect, useCallback } from 'react'
import { Dialog, DialogBackdrop } from '@headlessui/react'
import { useDispatch } from 'react-redux'
import { AsyncSelect } from '@atlaskit/select'
import Button from '@atlaskit/button'
import TextField from '@atlaskit/textfield'
import Avatar from '@atlaskit/avatar'
import { token } from '@atlaskit/tokens'
import { createTestRunAsync } from '../../store/slices/testScenarioSlice'
import { searchJiraUsers } from '../../utils/jiraContext'

/**
 * NEW RUN MODAL
 *
 * Modal for creating a new test run with custom fields.
 * Dynamically renders fields based on custom field definitions from API.
 *
 * Field Types:
 * - TEXT: Renders as TextField input
 * - USER: Renders as AsyncSelect user picker (like assignee)
 * - TIMESTAMP: Not shown (not editable by user)
 *
 * @param {boolean} isOpen - Whether modal is open
 * @param {function} onClose - Callback to close modal
 * @param {number} testScenarioId - ID of test scenario to create run for
 * @param {Array} customFields - Array of custom field definitions from API
 */
export function NewRunModal({ isOpen, onClose, testScenarioId, customFields }) {
  const dispatch = useDispatch()
  const portalTarget = useMemo(
    () => (typeof document !== 'undefined' ? document.body : null),
    []
  )
  const selectStyles = useMemo(
    () => ({
      menuPortal: (base) => ({ ...base, zIndex: 2000 }),
    }),
    []
  )

  // Local state for field values - keyed by fieldName
  const [fieldValues, setFieldValues] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Filter and sort fields: only show editable fields (not TIMESTAMP), ordered by orderIndex
  const editableFields = useMemo(() => {
    if (!customFields) return []
    return customFields
      .filter(field => field.showField && field.fieldType !== 'TIMESTAMP')
      .sort((a, b) => a.orderIndex - b.orderIndex)
  }, [customFields])

  // Reset field values when modal opens
  useEffect(() => {
    if (isOpen) {
      setFieldValues({})
      setIsSubmitting(false)
    }
  }, [isOpen])

  // Unassigned option for USER fields
  const UNASSIGNED_OPTION = {
    label: 'Unassigned',
    value: null,
    avatarUrl: null,
    isUnassigned: true
  }

  /**
   * LOAD USER OPTIONS
   *
   * Async function to fetch Jira users based on search query.
   * Includes "Unassigned" option at the top.
   */
  const loadUserOptions = useCallback(async (inputValue) => {
    const users = await searchJiraUsers(inputValue)
    const userOptions = users.map(user => ({
      label: user.displayName,
      value: user.accountId,
      avatarUrl: user.avatarUrl
    }))
    return [UNASSIGNED_OPTION, ...userOptions]
  }, [])

  /**
   * FORMAT USER OPTION
   *
   * Custom render for user options showing avatar + name
   */
  const formatUserOption = useCallback(({ label, avatarUrl, isUnassigned }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {isUnassigned ? (
        <Avatar size="small" name="?" />
      ) : (
        <Avatar size="small" src={avatarUrl} name={label} />
      )}
      <span style={isUnassigned ? { color: token('color.text.subtle'), fontStyle: 'italic' } : undefined}>
        {label}
      </span>
    </div>
  ), [])

  /**
   * HANDLE TEXT FIELD CHANGE
   */
  const handleTextChange = (fieldName, value) => {
    setFieldValues(prev => ({
      ...prev,
      [fieldName]: value
    }))
  }

  /**
   * HANDLE USER FIELD CHANGE
   */
  const handleUserChange = (fieldName, selectedOption) => {
    setFieldValues(prev => ({
      ...prev,
      [fieldName]: selectedOption
    }))
  }

  /**
   * HANDLE CREATE RUN
   *
   * Builds the run data object and dispatches createTestRunAsync.
   * Field values are converted to strings (USER fields use accountId).
   */
  const handleCreateRun = async () => {
    if (!testScenarioId) return

    setIsSubmitting(true)

    try {
      // Build run data object with fieldName as keys
      const runData = {}

      editableFields.forEach(field => {
        const value = fieldValues[field.fieldName]

        if (field.fieldType === 'USER') {
          // USER field: use accountId string or null
          runData[field.fieldName] = value?.value || null
        } else if (field.fieldType === 'TEXT') {
          // TEXT field: use string value
          runData[field.fieldName] = value || ''
        }
      })

      await dispatch(createTestRunAsync({
        testScenarioId,
        runData
      })).unwrap()

      onClose()
    } catch (error) {
      console.error('Failed to create test run:', error)
      // Error is logged, user can try again
    } finally {
      setIsSubmitting(false)
    }
  }

  /**
   * RENDER FIELD
   *
   * Renders the appropriate input component based on fieldType
   */
  const renderField = (field) => {
    switch (field.fieldType) {
      case 'TEXT':
        return (
          <div key={field.id} style={{ marginBottom: '16px' }}>
            <label
              style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 500,
                color: token('color.text')
              }}
            >
              {field.fieldName}
            </label>
            <TextField
              value={fieldValues[field.fieldName] || ''}
              onChange={(e) => handleTextChange(field.fieldName, e.target.value)}
              placeholder={`Enter ${field.fieldName}`}
              width="100%"
            />
          </div>
        )

      case 'USER':
        return (
          <div key={field.id} style={{ marginBottom: '16px' }}>
            <label
              style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 500,
                color: token('color.text')
              }}
            >
              {field.fieldName}
            </label>
            <AsyncSelect
              cacheOptions
              defaultOptions={[UNASSIGNED_OPTION]}
              loadOptions={loadUserOptions}
              value={fieldValues[field.fieldName] || null}
              onChange={(option) => handleUserChange(field.fieldName, option)}
              placeholder="Search for a user..."
              isClearable
              formatOptionLabel={formatUserOption}
              menuPortalTarget={portalTarget}
              menuPlacement="auto"
              styles={selectStyles}
              noOptionsMessage={({ inputValue }) =>
                inputValue ? `No users found for "${inputValue}"` : 'Start typing to search users'
              }
            />
          </div>
        )

      default:
        return null
    }
  }

  return (
    <Dialog open={isOpen} onClose={onClose} className="headless-dialog-root">
      <DialogBackdrop className="headless-dialog-overlay" />

      <div className="headless-dialog-panel-wrapper">
        <Dialog.Panel
          key={`new-run-modal`}
          className="headless-dialog-panel"
          style={{
            width: '500px',
            maxHeight: '80vh',
            overflow: 'auto'
          }}
        >
          {/* HEADER */}
          <div className="headless-dialog-header">
            <Dialog.Title className="headless-dialog-title">
              Create New Run
            </Dialog.Title>
          </div>

          {/* BODY */}
          <div className="headless-dialog-body" style={{ padding: '20px' }}>
            {editableFields.length === 0 ? (
              <p style={{ color: token('color.text.subtle'), fontStyle: 'italic' }}>
                No editable fields available.
              </p>
            ) : (
              editableFields.map(field => renderField(field))
            )}
          </div>

          {/* FOOTER */}
          <div
            className="headless-dialog-footer"
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
              padding: '16px 20px',
              borderTop: `1px solid ${token('color.border')}`
            }}
          >
            <Button
              appearance="subtle"
              onClick={onClose}
              isDisabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={handleCreateRun}
              isLoading={isSubmitting}
              isDisabled={isSubmitting}
            >
              Create Run
            </Button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}
